
import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft, Search } from "lucide-react";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink 
} from "@/components/ui/pagination";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    // Log errors for monitoring
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
    
    // Report 404s to analytics if available
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as any).gtag('event', 'page_404', {
        'event_category': 'error',
        'event_label': location.pathname,
        'non_interaction': true
      });
    }
  }, [location.pathname]);

  // Suggested pages to help users find what they're looking for
  const suggestions = [
    { path: "/", label: "Home" },
    { path: "/blog", label: "Blog" },
    { path: "/courses", label: "Courses" },
    { path: "/projects", label: "Projects" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" }
  ];

  return (
    <Layout 
      title="Page Not Found - 404 Error | Freelance With Andy" 
      description="We couldn't find the page you're looking for. Please navigate to one of our main sections or go back to the homepage."
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 flex flex-col items-center justify-center text-center">
        <div className="mb-8">
          <Search className="h-20 w-20 text-gray-300 mx-auto" />
        </div>
        <h1 className="text-6xl font-bold text-brand-green mb-4">404</h1>
        <h2 className="text-3xl font-semibold mb-6">Page Not Found</h2>
        <p className="text-gray-600 mb-10 max-w-md">
          Sorry, we couldn't find the page you're looking for. The page might have been removed, 
          had its name changed, or is temporarily unavailable.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Link to="/">
            <Button className="gap-2 min-w-[160px]">
              <Home size={18} />
              Back to Home
            </Button>
          </Link>
          <Button 
            variant="outline" 
            className="gap-2 min-w-[160px]"
            onClick={() => window.history.back()}
          >
            <ArrowLeft size={18} />
            Go Back
          </Button>
        </div>
        
        <div className="mt-8 w-full max-w-lg">
          <h3 className="text-lg font-medium mb-4">Popular Pages</h3>
          <Pagination>
            <PaginationContent>
              {suggestions.map((link) => (
                <PaginationItem key={link.path}>
                  <PaginationLink href={link.path}>
                    {link.label}
                  </PaginationLink>
                </PaginationItem>
              ))}
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </Layout>
  );
};

export default NotFound;
